<template>
    <SmallRightColumn>
        <template #left>
            <div class="flex sm:items-center sm:flex-row flex-col mb-8 gap-8" v-if="product.brand">
                <div>
                    <BrandImage
                        width="220"
                        class="hidden sm:flex w-[220px]"
                        :brand="product.brand"></BrandImage>

                    <img :src="product.brand.image" class="block sm:hidden w-auto h-24 mx-auto" height="96">
                </div>

                <div>
                    <AppButton theme="secondary"
                               size="small"
                               :href="route('brands.show', product.brand)">
                        View all {{ product.brand.name }} tyres
                    </AppButton>
                </div>
            </div>

            <div class="flex flex-col md:flex-row">
                <div class="flex-grow">
                    <h1 class="font-bold text-5xl mb-2 text-dark">
                        <template v-if="seoTags?.h1_tag">{{ seoTags.h1_tag }}</template>
                        <template v-else>{{ product.brand.name }} {{ product.name }} Reviews</template>
                    </h1>
                    <p class="font-bold text-dark mb-4">
                        {{ product.reviews_count.toLocaleString() }} reviews
                    </p>

                    <div class="flex">
                        <AppButton class="mr-4" @click="scrollToRef('latest-reviews')">Read Reviews</AppButton>
<!--                        <AppButton theme="secondary">View Sizes</AppButton>-->
                    </div>

                    <SupersededBy v-if="product.superseded_by" :product="product.superseded_by"></SupersededBy>

                    <p class="font-bold text-dark mb-4 mt-4">
                        About the {{ product.brand.name }} {{ product.name }}
                    </p>

                    <p class="text-dark" v-html="product.description"></p>
                </div>
                <div class="flex-none hidden md:block md:basis-1/3 relative">
                    <div class="absolute left-0" v-if="product.top_badges.length">
                        <div v-for="( badge, counter) in product.top_badges">
                            <img class="absolute round-full max-w-[85px]" :class="counter === 0 ? 'top-0' : 'top-36'"
                                 :src="badge.path"
                                 :alt="badge.name"
                            />
                        </div>
                    </div>
                    <img class="max-h-40 md:max-h-fit" :src="product.image">
                </div>
            </div>

            <TyreSizeLookup
                v-if="product.brand && product.brand.prioritised"
                :registration="pastInput.registration"
                :product="product"></TyreSizeLookup>

            <TyreSizeLookup
                v-if="!product.brand || !product.brand.prioritised"
                :registration="pastInput.registration"
                :product="product"></TyreSizeLookup>

            <!--            <AppContainer class="!p-0" v-if="product.video_title && product.video_clip_id">-->
            <!--                <div class="w-full md:w-8/12">-->
            <!--                    <span class="font-bold text-sm block text-2xl my-5">{{ product.video_title }}</span>-->
            <!--                </div>-->
            <!--                <AppVideo :type="product.video_type" :clip="product.video_clip_id"></AppVideo>-->
            <!--            </AppContainer>-->

            <VideosGrid :videos="product.videos"></VideosGrid>

            <SimilarTyres v-if="product.comparisons && product.comparisons.length > 0"
                          :product="product"
                          :comparisons="product.comparisons"></SimilarTyres>

            <!-- This is exactly the same as the other component, but will only show on mobile -->
            <ProductQuestionRatings
                class="lg:hidden"
                :questions="questions"
                :product="product"
            ></ProductQuestionRatings>

            <TopProductReviews :product="product"></TopProductReviews>

            <LatestProductReviews ref="latest-reviews" :product="product"></LatestProductReviews>
        </template>

        <template #right v-if="questions">
            <ProductQuestionRatings
                class="!sticky w-full top-24"
                :questions="questions"
                :product="product"
            ></ProductQuestionRatings>
        </template>
    </SmallRightColumn>

    <AppSchema :schema="schema"></AppSchema>
</template>

<script>
import {Head} from "@inertiajs/inertia-vue3";
import AppVideo from "@/Components/AppVideo";
import MainLayout from "@/Layouts/MainLayout";
import BrandImage from "@/Components/Brands/BrandImage";
import AppButton from "@/Components/AppButton";
import ProductQuestionRatings from "@/Components/Products/ProductQuestionRatings";
import {useProductQuestions} from "@/Composables/useProductQuestions";
import SmallRightColumn from "@/Layouts/SmallRightColumn";
import TyreSizeLookup from "@/Components/Products/TyreSizeLookup/ProductTyreSizeLookup";
import SimilarTyres from "@/Components/Products/Tyres/SimilarTyres";
import TopProductReviews from "@/Components/Products/TopProductReviews";
import LatestProductReviews from "@/Components/Products/LatestProductReviews";
import SupersededBy from "@/Components/Products/Tyres/SupersededBy";
import AppContainer from "@/Layouts/AppContainer";
import Scroll from "@/Mixins/Utilities/Scroll";
import VideosGrid from "@/Components/Videos/VideosGrid";
import AppHead from "@/Components/AppSeoTags";
import AppSchema from "@/Components/AppSchema";

export default {
    components: {
        AppSchema,
        AppHead,
        VideosGrid,
        AppContainer,
        AppVideo,
        LatestProductReviews,
        TopProductReviews,
        TyreSizeLookup, ProductQuestionRatings, AppButton, BrandImage, SmallRightColumn, SimilarTyres,
        SupersededBy,
        Head
    },
    layout: MainLayout,
    mixins: [Scroll],
    props: {
        product: Object,
        seoTags: Object,
        questions: Array,
        prices: Array,
        pastInput: Object
    },
    setup(props) {
        const schema = {
            "@context": "https://schema.org",
            "@type": "Product",
            "name": props.product.headline_name,
            "description": props.product.description,
            "image": props.product.image,
            "brand": {
                "@type": "thing",
                "name": props.product.brand.name
            },
            "offers": {
                "@type": "AggregateOffer",
                "highPrice": props.product.min_max_price.highest_price,
                "lowPrice": props.product.min_max_price.lowest_price,
                "priceCurrency": "GBP",
                "offerCount": props.product.tyre_details_count
            },
            "aggregateRating": {
                "@type": "AggregateRating",
                "ratingValue": (props.product.reviews_avg_rating / 2).toFixed(2),
                "reviewCount": props.product.reviews_count
            }
        };

        return {
            schema
        };
    }
}
</script>

<style scoped>

</style>
